import React from 'react';
import { Alert, Button, Chip } from '@mui/material';
import { MdCurrencyRupee, MdPhoneAndroid, MdLocalAtm, MdAccountBalanceWallet, MdPendingActions, MdErrorOutline, MdVerified, MdStars } from 'react-icons/md';
import TabbedReport, { pill, TwoLine, Dash, pctChip, shareBar } from './TabbedReport';
import { inr, fmtDate } from './reportShared';

const METHODS = { upi: 'UPI', card: 'Card', netbanking: 'Net Banking', wallet: 'Wallet', reward: 'Reward Points', cod: 'COD', cash: 'Cash', others: 'Others' };
const STATUS = { success: ['#dcfce7', '#166534', 'Success'], pending: ['#fef3c7', '#92400e', 'Pending'], failed: ['#fee2e2', '#991b1b', 'Failed'], processing: ['#e0f2fe', '#075985', 'Processing'], paid: ['#dcfce7', '#166534', 'Paid'] };
const statusChip = (s) => {
  const [bg, fg, text] = STATUS[s] || ['#f1f5f9', '#475569', s];
  return <Chip size="small" label={text} sx={pill(bg, fg)} />;
};
const customerCol = { key: 'customer', label: 'Customer', text: (r) => r.customer, cell: (r) => <TwoLine top={r.customer} bottom={r.phone} />, extra: (r) => ({ Phone: r.phone }) };
const money = (key, label, bold) => ({ key, label, numeric: true, bold, text: (r) => r[key], cell: (r) => inr(r[key]), print: (r) => inr(r[key]) });

const tabs = [
  {
    key: 'transactions',
    label: 'Transactions',
    defaultSort: 'date_desc',
    sorts: [['date_desc', 'Newest first'], ['date_asc', 'Oldest first'], ['amount_desc', 'Amount: high to low']],
    rowAlert: (r) => r.status === 'failed',
    columns: [
      { key: 'orderNumber', label: 'Order No.', bold: true, text: (r) => r.orderNumber },
      { key: 'date', label: 'Order Date', text: (r) => fmtDate(r.date) },
      customerCol,
      { key: 'method', label: 'Method', text: (r) => METHODS[r.method] || r.method, cell: (r) => <Chip size="small" label={METHODS[r.method] || r.method} sx={pill('#e0e7ff', '#3730a3')} /> },
      { key: 'provider', label: 'Gateway', text: (r) => r.provider },
      { key: 'reference', label: 'Reference', text: (r) => r.reference, cell: (r) => r.reference || <Dash /> },
      money('amount', 'Amount', true),
      { key: 'status', label: 'Status', text: (r) => STATUS[r.status]?.[2] || r.status, cell: (r) => statusChip(r.status) },
      { key: 'paidAt', label: 'Paid At', text: (r) => (r.paidAt ? fmtDate(r.paidAt) : ''), cell: (r) => (r.paidAt ? fmtDate(r.paidAt) : <Dash />) },
    ],
  },
  {
    key: 'methods',
    label: 'By Method',
    columns: [
      { key: 'label', label: 'Payment Method', bold: true, text: (r) => r.label },
      { key: 'transactions', label: 'Transactions', numeric: true, text: (r) => r.transactions },
      { key: 'successful', label: 'Successful', numeric: true, text: (r) => r.successful },
      { key: 'failed', label: 'Failed', numeric: true, text: (r) => r.failed, cell: (r) => (r.failed ? <Chip size="small" label={r.failed} sx={pill('#fee2e2', '#b91c1c')} /> : 0) },
      { key: 'pending', label: 'Pending', numeric: true, text: (r) => r.pending },
      { key: 'successRate', label: 'Success Rate', center: true, text: (r) => (r.successRate === null ? '' : `${r.successRate}%`), cell: (r) => pctChip(r.successRate, { good: 95, warn: 85 }) },
      money('amount', 'Collected', true),
      { key: 'sharePct', label: '% of Collections', text: (r) => `${r.sharePct}%`, cell: (r) => shareBar(r.sharePct) },
      money('avgAmount', 'Avg Payment'),
    ],
  },
  {
    key: 'cod',
    label: 'COD to Collect',
    emptyText: 'No delivered COD orders are waiting to be marked paid.',
    rowAlert: (r) => r.daysSinceOrder > 7,
    columns: [
      { key: 'orderNumber', label: 'Order No.', bold: true, text: (r) => r.orderNumber },
      { key: 'date', label: 'Order Date', text: (r) => fmtDate(r.date) },
      customerCol,
      money('amount', 'Amount to Collect', true),
      { key: 'paymentStatus', label: 'Payment Status', text: (r) => STATUS[r.paymentStatus]?.[2] || r.paymentStatus, cell: (r) => statusChip(r.paymentStatus) },
      { key: 'daysSinceOrder', label: 'Days Since Order', center: true, text: (r) => r.daysSinceOrder, cell: (r) => <Chip size="small" label={`${r.daysSinceOrder} days`} sx={r.daysSinceOrder > 7 ? pill('#fee2e2', '#b91c1c') : pill('#fef3c7', '#92400e')} /> },
    ],
  },
  {
    key: 'issues',
    label: 'Payment Issues',
    emptyText: 'No failed or stuck payments. 🎉',
    rowAlert: () => true,
    columns: [
      { key: 'orderNumber', label: 'Order No.', bold: true, text: (r) => r.orderNumber },
      { key: 'date', label: 'Order Date', text: (r) => fmtDate(r.date) },
      customerCol,
      money('amount', 'Order Value', true),
      { key: 'paymentStatus', label: 'Payment Status', text: (r) => STATUS[r.paymentStatus]?.[2] || r.paymentStatus, cell: (r) => statusChip(r.paymentStatus) },
      { key: 'orderStatus', label: 'Order Status', text: (r) => r.orderStatus },
      { key: 'attempts', label: 'Payment Attempts', numeric: true, text: (r) => r.attempts },
      { key: 'lastProvider', label: 'Gateway', text: (r) => r.lastProvider },
    ],
  },
];

const PaymentReport = () => (
  <TabbedReport
    route="reports/payments-report"
    title="Payment Report"
    subtitle="Money collected by method, COD still to collect, and payments that failed or got stuck"
    filePrefix="Payments"
    tabs={tabs}
    searchLabel="Search order no. / customer / phone / reference"
    filters={[
      { key: 'method', label: 'Method', tabs: ['transactions'], options: Object.entries(METHODS).map(([value, label]) => ({ value, label })) },
      { key: 'pstatus', label: 'Payment status', tabs: ['transactions'], options: [{ value: 'success', label: 'Success' }, { value: 'pending', label: 'Pending' }, { value: 'failed', label: 'Failed' }] },
    ]}
    cards={(s, { goTo, isActive }) => [
      { label: 'Collected', value: inr(s.collected), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `${s.transactions || 0} successful payments`, onClick: () => goTo('transactions', { pstatus: 'success' }), active: isActive('transactions', { pstatus: 'success' }) },
      { label: 'Online Payments', value: inr(s.online), icon: MdPhoneAndroid, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: 'UPI, card, net banking' },
      { label: 'COD Collected', value: inr(s.codCollected), icon: MdLocalAtm, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Cash on delivery received' },
      { label: 'COD to Collect', value: inr(s.codPending), icon: MdPendingActions, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `${s.codPendingCount || 0} delivered orders not marked paid`, onClick: () => goTo('cod'), active: isActive('cod') },
      { label: 'Payment Issues', value: ((s.failedCount || 0) + (s.stuckCount || 0)).toLocaleString('en-IN'), icon: MdErrorOutline, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${s.failedCount || 0} failed · ${s.stuckCount || 0} stuck`, onClick: () => goTo('issues'), active: isActive('issues') },
      { label: 'Success Rate', value: s.successRate === null || s.successRate === undefined ? '—' : `${s.successRate}%`, icon: MdVerified, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: s.topMethod ? `Most used: ${s.topMethod}` : 'Of completed attempts', onClick: () => goTo('methods'), active: isActive('methods') },
      { label: 'Wallet & Rewards Used', value: inr((s.walletUsed || 0) + (s.rewardUsed || 0)), icon: MdAccountBalanceWallet, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: `Wallet ${inr(s.walletUsed)} · rewards ${inr(s.rewardUsed)}` },
    ]}
    banner={(s, { goTo }) =>
      s.codPending > 0 ? (
        <Alert severity="warning" icon={<MdStars />} action={<Button color="inherit" size="small" onClick={() => goTo('cod')}>Review</Button>}>
          {inr(s.codPending)} from {s.codPendingCount} delivered COD order(s) hasn't been marked as collected — check with the courier's COD remittance.
        </Alert>
      ) : null
    }
  />
);

export default PaymentReport;
